import { useCallback, useState, useRef, useEffect } from 'react';
import { X, CheckCircle, AlertTriangle, XCircle, Info, Loader, ChevronDown, ChevronRight, Trash2 } from 'lucide-react';
import type { MistDiagnostic, MistDiagnosticCategory } from '../../editor/diagnosticTypes';
import { CATEGORY_LABELS, STAGE_LABELS } from '../../editor/diagnosticCodes';
import styles from './ProblemsPanel.module.css';

export interface LogEntry {
  time: string;
  message: string;
  level: 'info' | 'success' | 'error';
}

interface ProblemsPanelProps {
  diagnostics: MistDiagnostic[];
  fileName: string | null;
  onClose: () => void;
  onGoToLine?: (line: number, column: number) => void;
  analyzing?: boolean;
  outputLog?: LogEntry[];
  activeTab?: 'problems' | 'output';
  onTabChange?: (tab: 'problems' | 'output') => void;
  onClearLog?: () => void;
}

const SEVERITY_ICONS: Record<string, React.ReactNode> = {
  Error: <XCircle size={12} />,
  Warning: <AlertTriangle size={12} />,
  Info: <Info size={12} />,
};

const SEVERITY_CLASSES: Record<string, string> = {
  Error: styles.severityError,
  Warning: styles.severityWarning,
  Info: styles.severityInfo,
};

const CATEGORY_STYLE_MAP: Partial<Record<MistDiagnosticCategory, string>> = {
  Syntax: styles.categorySyntax,
  ParseStructure: styles.categorySyntax,
  Type: styles.categoryType,
  NameResolution: styles.categoryType,
  ContractStructure: styles.categoryContract,
  ValueFlow: styles.categoryValueFlow,
  Determinism: styles.categoryDeterminism,
  Policy: styles.categoryDeterminism,
  PviLowering: styles.categoryValueFlow,
  UvlInvariant: styles.categoryValueFlow,
  Build: styles.categoryBuild,
  DeployPreflight: styles.categoryDeploy,
  Simulation: styles.categorySim,
  Adversarial: styles.categoryAdv,
  Internal: styles.categoryInternal,
};

export function ProblemsPanel({ diagnostics, fileName, onClose, onGoToLine, analyzing, outputLog = [], activeTab = 'problems', onTabChange, onClearLog }: ProblemsPanelProps) {
  const errorCount = diagnostics.filter(d => d.severity === 'Error').length;
  const warningCount = diagnostics.filter(d => d.severity === 'Warning').length;
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const logEndRef = useRef<HTMLDivElement>(null);

  // Auto-scroll output log to bottom
  useEffect(() => {
    if (activeTab === 'output') {
      logEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }
  }, [outputLog.length, activeTab]);

  const handleClick = useCallback((d: MistDiagnostic) => {
    if (d.range) {
      onGoToLine?.(d.range.startLine, d.range.startColumn);
    }
  }, [onGoToLine]);

  const toggleExpand = useCallback((id: string) => {
    setExpandedId(prev => prev === id ? null : id);
  }, []);

  // Sort: errors first, then warnings, then info. Within same severity, by line.
  const sorted = [...diagnostics].sort((a, b) => {
    const sevOrder = { Error: 0, Warning: 1, Info: 2 };
    const sa = sevOrder[a.severity] ?? 2;
    const sb = sevOrder[b.severity] ?? 2;
    if (sa !== sb) return sa - sb;
    return (a.range?.startLine ?? 0) - (b.range?.startLine ?? 0);
  });

  // Group by file (currently single file)
  const fileLabel = fileName ?? 'File';

  return (
    <div className={styles.panel}>
      <div className={styles.header}>
        <div className={styles.headerLeft}>
          <button
            className={`${styles.tabBtn} ${activeTab === 'problems' ? styles.tabBtnActive : ''}`}
            onClick={() => onTabChange?.('problems')}
          >
            Problems
            {errorCount > 0 && <span className={`${styles.badge} ${styles.badgeError}`}>{errorCount}</span>}
            {warningCount > 0 && <span className={`${styles.badge} ${styles.badgeWarning}`}>{warningCount}</span>}
          </button>
          <button
            className={`${styles.tabBtn} ${activeTab === 'output' ? styles.tabBtnActive : ''}`}
            onClick={() => onTabChange?.('output')}
          >
            Output
          </button>
        </div>
        <div className={styles.headerRight}>
          {activeTab === 'output' && outputLog.length > 0 && (
            <button className={styles.closeBtn} onClick={onClearLog} title="Clear Output" aria-label="Clear Output">
              <Trash2 size={12} />
            </button>
          )}
          <button className={styles.closeBtn} onClick={onClose} title="Close Panel" aria-label="Close Panel">
            <X size={14} />
          </button>
        </div>
      </div>

      {/* Output tab */}
      {activeTab === 'output' && (
        <div className={styles.outputBody}>
          {outputLog.length === 0 && (
            <div className={styles.emptyState}>
              <span>No output yet. Click Compile or Deploy to see results here.</span>
            </div>
          )}
          {outputLog.map((entry, i) => (
            <div key={i} className={`${styles.logLine} ${entry.level === 'error' ? styles.logError : entry.level === 'success' ? styles.logSuccess : ''}`}>
              <span className={styles.logTime}>[{entry.time}]</span>
              <span className={styles.logMsg}>{entry.message}</span>
            </div>
          ))}
          <div ref={logEndRef} />
        </div>
      )}

      {/* Problems tab */}
      {activeTab === 'problems' && (
      <div className={styles.body}>
        {/* Analyzing state */}
        {analyzing && (
          <div className={styles.analyzingState}>
            <Loader size={12} className={styles.spin} />
            <span>Analyzing Mist source…</span>
          </div>
        )}

        {/* Empty / clean state */}
        {!analyzing && diagnostics.length === 0 && (
          <div className={styles.emptyState}>
            <CheckCircle size={14} />
            <span>✔ No issues detected</span>
          </div>
        )}

        {/* Diagnostic list */}
        {sorted.length > 0 && (
          <div className={styles.fileGroup}>
            <div className={styles.fileHeader}>
              <span>{fileLabel}</span>
              <span className={styles.badge}>{sorted.length}</span>
            </div>
            {sorted.map((d, i) => {
              const id = `${d.code}-${i}`;
              const isExpanded = expandedId === id;
              const hasDetail = !!(d.detail || d.explanation || (d.relatedRanges && d.relatedRanges.length > 0) || d.deployBlocking || d.simulationBlocking);
              const stageLabel = STAGE_LABELS[d.stage] ?? d.stage;
              const catLabel = CATEGORY_LABELS[d.category] ?? d.category;
              const catClass = CATEGORY_STYLE_MAP[d.category] ?? styles.categoryInternal;

              return (
                <div key={id}>
                  <div
                    className={styles.diagnosticRow}
                    onClick={() => handleClick(d)}
                    onMouseDown={e => e.preventDefault()}
                  >
                    {/* Expand toggle */}
                    {hasDetail ? (
                      <button
                        className={styles.expandBtn}
                        onClick={(e) => { e.stopPropagation(); toggleExpand(id); }}
                        aria-label={isExpanded ? 'Collapse' : 'Expand'}
                        tabIndex={-1}
                      >
                        {isExpanded ? <ChevronDown size={10} /> : <ChevronRight size={10} />}
                      </button>
                    ) : (
                      <span className={styles.expandSpacer} />
                    )}
                    <span className={`${styles.severityIcon} ${SEVERITY_CLASSES[d.severity]}`}>
                      {SEVERITY_ICONS[d.severity]}
                    </span>
                    <span className={styles.code}>{d.code}</span>
                    <span className={styles.message}>{d.message}</span>
                    <span className={`${styles.stageTag} ${catClass}`}>{catLabel}</span>
                    <span className={styles.stageLabel}>{stageLabel}</span>
                    {d.range && (
                      <span className={styles.location}>{d.range.startLine}:{d.range.startColumn}</span>
                    )}
                  </div>

                  {/* Expanded detail */}
                  {isExpanded && hasDetail && (
                    <div className={styles.detailBox}>
                      {/* Blocking badges */}
                      <div className={styles.blockingRow}>
                        {d.deployBlocking && <span className={`${styles.blockBadge} ${styles.blockDeploy}`}>Deploy: Blocked</span>}
                        {d.simulationBlocking && <span className={`${styles.blockBadge} ${styles.blockSim}`}>Simulation: Blocked</span>}
                        {d.graphBlocking && <span className={`${styles.blockBadge} ${styles.blockGraph}`}>Graph: Blocked</span>}
                        {!d.deployBlocking && !d.simulationBlocking && !d.graphBlocking && (
                          <span className={`${styles.blockBadge} ${styles.blockNone}`}>Non-blocking</span>
                        )}
                      </div>

                      {/* Detail text */}
                      {d.detail && <p className={styles.detailText}>{d.detail}</p>}

                      {/* Explanation */}
                      {d.explanation && (
                        <div className={styles.explanation}>
                          <strong className={styles.explainTitle}>{d.explanation.title}</strong>
                          <ul className={styles.factList}>
                            {d.explanation.facts.map((f, fi) => <li key={fi}>{f}</li>)}
                          </ul>
                          {d.explanation.values && Object.keys(d.explanation.values).length > 0 && (
                            <div className={styles.valueGrid}>
                              {Object.entries(d.explanation.values).map(([k, v]) => (
                                <div key={k} className={styles.valueRow}>
                                  <span className={styles.valueKey}>{k}</span>
                                  <span className={styles.valueVal}>{String(v)}</span>
                                </div>
                              ))}
                            </div>
                          )}
                          {d.explanation.invariant && (
                            <p className={styles.invariant}>Invariant: {d.explanation.invariant}</p>
                          )}
                        </div>
                      )}

                      {/* Related ranges */}
                      {d.relatedRanges && d.relatedRanges.length > 0 && (
                        <div className={styles.relatedSection}>
                          <span className={styles.relatedLabel}>Related locations:</span>
                          {d.relatedRanges.map((r, ri) => (
                            <button
                              key={ri}
                              className={styles.relatedItem}
                              onClick={() => onGoToLine?.(r.range.startLine, r.range.startColumn)}
                              tabIndex={-1}
                            >
                              Line {r.range.startLine}:{r.range.startColumn} — {r.message}
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
      )}
    </div>
  );
}
